import type { z } from 'zod'

export abstract class GarItem<T extends string, D> {
  /** @description FIAS record type */
  abstract readonly type: T

  /** @description Path to XML file */
  readonly filePath: string

  /** @description Unprocessed FIAS record attributes */
  readonly raw: Readonly<Record<string, string>>

  /** @description FIAS record data */
  readonly data: D

  /** @description Schema for data validation */
  protected abstract get schema(): GarItem.Schema<D>

  /** @description Constructor for creating a GarItem instance */
  constructor(options: GarItem.ParseOptions) {
    this.filePath = options.filePath
    this.raw = options.attributes
    this.data = this.parse()
  }

  /** @description Parses raw data using the schema */
  private parse(): D {
    return this.schema.parse(this.raw)
  }

  /** @description Returns the object in JSON format */
  toJSON(): GarItem.Result<T, D> {
    return {
      type: this.type,
      data: this.data,
    }
  }
}

export namespace GarItem {
  export type Schema<T> = z.ZodType<T, Record<string, string>>

  export interface ParseOptions {
    /** @description Path to XML file */
    readonly filePath: string

    /** @description Unprocessed FIAS record attributes */
    readonly attributes: Readonly<Record<string, string>>
  }

  export interface Result<T extends string, D> {
    /** @description FIAS record type */
    readonly type: T

    /** @description FIAS record data */
    readonly data: D
  }
}
